import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardBody, Col, Container, CardHeader, Row } from 'reactstrap';
// import { useNavigate } from 'react-router-dom';

import BreadCrumb from 'Components/Common/BreadCrumb';
// import { isEmpty } from 'lodash';

// Export Modal
// import ExportCSVModal from 'Components/Common/ExportCSVModal';

import Loader from 'Components/Common/Loader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { RootState } from 'slices';
// import { createSortableHeader } from 'Components/pos2u/CreateSortableHeader';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import ModalBackdropNotFound from 'Components/pos2u/ModalBackdropNotFound';
import {
  setShowAllValidateErrors,
  resetProductOption,
} from 'slices/productOption/reducer';
import { useDispatch } from 'react-redux';

import {
  setCurrentPage,
  setEntriesPerPage,
  setInputPageValue,
  setTotalProductOptions,
} from 'slices/productOption/reducer';
import DragHandle from 'Components/pos2u/TableElements/DragHandle';
import { ProductOptionDetail } from 'types/product/productOptionTypes';
import { useNavigate } from 'react-router-dom';
import ProductOptionsTable from 'pages/ProductOptions/ProductOptionsTable';
import {
  deleteProductOptionById,
  getProductOptionList,
} from 'services/ProductOptionService';
import {
  PRODUCT_OPTIOINS_LIMIT,
  ProductOptionActionButtons,
  ProductOptionTableColumnWidth,
  RequirementOptions,
  SelectionMethodOptions,
} from './ProductOptionConstants';
import DeleteModal from 'Components/Common/DeleteModal';
import IconActionButton from 'Components/pos2u/Buttons/IconActionButton';
import { FaRegPenToSquare } from 'react-icons/fa6';
import DeleteIconWithDisabledButton from 'Components/pos2u/Buttons/DeleteIconWithDisabledButton';
import MainAddButton from 'Components/pos2u/Buttons/MainAddButton';

const ProductOptionsPage = () => {
  const { t } = useTranslation();
  const history = useNavigate();
  const dispatch = useDispatch();

  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';

  // const infoState = useSelector((state: RootState) => state.Info);
  // const { merchant, isMerchantLoaded } = infoState;

  const ProductOptionState = useSelector(
    (state: RootState) => state.ProductOption
  );
  const { PageDetails } = ProductOptionState;

  const columnHelper = createColumnHelper<ProductOptionDetail>();

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [currentReqName, setCurrentReqName] = useState<string>('');
  const [currentReqId, setCurrentReqId] = useState<number | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);
  // const [categoriesList, setCategoriesList] = useState<CategoryFromAPI[]>([]);
  const [productOptionList, setProductOptionList] = useState<
    ProductOptionDetail[]
  >([]);
  // const [productOptionTotal, setCategoriesTotal] = useState<number>(0);
  const [productOptionTotal, setProductOptionTotal] = useState<number>(0);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);

  const fetchProductOptionList = useCallback(async () => {
    setIsLoadSuccess(false);
    try {
      const response = await getProductOptionList();

      if (response) {
        const { message, error_code, data } = response;
        if (message === 'OK' && error_code === 0) {
          const { product_options, total } = data;
          setProductOptionTotal(total);
          dispatch(setTotalProductOptions(total));
          setProductOptionList(product_options);

          setIsNotFound(total === 0);
        } else {
          setProductOptionTotal(0);
          dispatch(setTotalProductOptions(0));
          setProductOptionList([]);
        }
        setIsLoadSuccess(true);
      } else {
        console.error('No response data returned');
        setProductOptionTotal(0);
        dispatch(setTotalProductOptions(0));
        setProductOptionList([]);
        setIsLoadSuccess(true);
      }
    } catch (error) {
      console.error('Failed to fetch product option data:', error);
      setProductOptionTotal(0);
      setProductOptionList([]);
      setIsLoadSuccess(true);
    }
  }, [dispatch]);

  const deleteProductOption = useCallback(
    async (id: string) => {
      try {
        const response: any = await deleteProductOptionById(id);
        if (response) {
          const { message, error_code, data } = response;
          if (message === 'OK' && error_code === 0) {
            toast.success(data.message, { autoClose: 3000 });
            fetchProductOptionList();
          } else {
            toast.error(message, { autoClose: 3000 });
          }
        } else {
          toast.error('No response data returned', { autoClose: 3000 });
          console.error('No response data returned');
        }
      } catch (error) {
        console.log(error);
        toast.error('Unexpected error occurred. Please try again.', {
          autoClose: 3000,
        });
      }
    },
    [fetchProductOptionList]
  );

  useEffect(() => {
    fetchProductOptionList();
  }, [fetchProductOptionList]);

  useEffect(() => {
    if (productOptionList) {
      setIsLoadSuccess(true);
    } else {
      setIsLoadSuccess(false);
    }
  }, [productOptionList]);

  const ProductOptionColumns: ColumnDef<ProductOptionDetail, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: 'drag',
        meta: {
          style: { width: ProductOptionTableColumnWidth.drag },
        },
        header: t('Order'),
        cell: (cell) => <DragHandle id={cell.row.original.id} />,
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('name', {
        header: t('Option Name'),
        meta: {
          style: { width: ProductOptionTableColumnWidth.name },
        },
        cell: (cell) => cell.getValue(),
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'product_sub_options_name',
        meta: {
          style: {
            width: ProductOptionTableColumnWidth.product_sub_options_name,
          },
        },
        header: () => t('Sub-option'),
        cell: ({ row }) => (
          <ul style={{ padding: 0, margin: 0, listStyleType: 'none' }}>
            {(row.original.options ?? []).map((value) => (
              <li key={value.id}>{value.name}</li>
            ))}
          </ul>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'product_sub_options_extra_price',
        meta: {
          style: {
            width:
              ProductOptionTableColumnWidth.product_sub_options_extra_price,
          },
        },
        header: () => t('Additional Price'),
        cell: ({ row }) => (
          <ul style={{ padding: 0, margin: 0, listStyleType: 'none' }}>
            {(row.original.options ?? []).map((value) => (
              <li key={value.id}>{value.extra_price.toFixed(2)}</li>
            ))}
          </ul>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('forced_type', {
        meta: {
          style: { width: ProductOptionTableColumnWidth.forced_type },
        },
        header: t('Required Selection'),
        cell: (cell) => {
          const data = cell.getValue().toString();
          const option = RequirementOptions(t).find(
            (opt) => opt.value === data
          );
          return option?.label ?? '';
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('option_type', {
        meta: {
          style: { width: ProductOptionTableColumnWidth.option_type },
        },
        header: t('Selection Method'),
        cell: (cell) => {
          const data = cell.getValue().toString();
          const option = SelectionMethodOptions(t).find(
            (opt) => opt.value === data
          );
          return option?.label ?? '';
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),

      columnHelper.display({
        id: 'action',
        meta: {
          style: { width: ProductOptionTableColumnWidth.action },
        },
        header: 'Action',
        cell: (cell) => (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
              <div className="d-flex gap-2">
                {/* <div
                  onClick={() => {
                    history(`/product-option/${cell.row.original.id}`);
                  }}
                  role="button"
                  style={{
                    display: 'flex',
                    cursor: 'pointer',
                    padding: '0',
                  }}
                  className="align-items-flex-start"
                >
      
                  <MdEdit fontSize={'1.25rem'} />
                </div> */}

                <IconActionButton
                  onClick={() =>
                    history(`/product-option/${cell.row.original.id}`)
                  }
                  icon={
                    <FaRegPenToSquare
                      fontSize={ProductOptionActionButtons.icon}
                    />
                  }
                  title={t('Edit')}
                  height={ProductOptionActionButtons.button.height}
                  width={ProductOptionActionButtons.button.width}
                />
                {/* <div
                  id={`btn-del-${cell.row.original.id}`}
                  onClick={() => {
                    if (!cell.row.original.deletable) return;
                    setCurrentReqName(cell.row.original.name);
                    setCurrentReqId(cell.row.original.id);
                    setDeleteModal(true);

                    // e.stopPropagation();
                    // e.preventDefault();
                  }}
                  role="button"
                  // style={{
                  //   display: 'flex',
                  //   cursor: 'pointer',
                  //   padding: '0',
                  // }}
                  style={{
                    display: 'flex',

                    cursor: cell.row.original.deletable
                      ? 'pointer'
                      : 'not-allowed',
                    padding: '0',
                    opacity: cell.row.original.deletable ? 1 : 0.5,
                    // pointerEvents: cell.row.original.deletable
                    //   ? 'auto'
                    //   : 'none',
                  }}
                  className="align-items-flex-start"
                >
                  <FaTrashAlt color="red" fontSize={'1.25rem'} />
                  {!cell.row.original.deletable && (
                    <UncontrolledTooltip
                      placement="top"
                      target={`btn-del-${cell.row.original.id}`}
                    >
                      {t('Product option is in use.')}
                    </UncontrolledTooltip>
                  )}
                </div> */}
                <DeleteIconWithDisabledButton
                  id={`btn-del-${cell.row.original.id}`}
                  onClick={() => {
                    if (!cell.row.original.deletable) return;
                    setCurrentReqName(cell.row.original.name);
                    setCurrentReqId(cell.row.original.id);
                    setDeleteModal(true);
                  }}
                  disabled={!cell.row.original.deletable}
                  tooltipWhenDisabled={t('Product option is in use.')}
                  height={ProductOptionActionButtons.button.height}
                  width={ProductOptionActionButtons.button.width}
                />
              </div>
            </li>
          </ul>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [columnHelper, t, history]
  );

  // Export Modal
  // const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  document.title = t('Product Option List') + ' | POS2U';

  const totalPages = Math.ceil(productOptionTotal / PageDetails.entriesPerPage);

  const handlePageChange = useCallback(
    (newPage: number) => {
      dispatch(setCurrentPage(newPage));
    },
    [dispatch]
  );

  return (
    <div className="page-content">
      {/* <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={orders}
      /> */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => {
          setDeleteModal(false);
          deleteProductOption(currentReqId ? currentReqId.toString() : '');
        }}
        onCloseClick={() => setDeleteModal(false)}
        recordId={currentReqName}
        title={t('Delete product option?')}
        message={t('Confirm to delete product option')}
        cancelBtnMsg={t('Cancel')}
        confirmBtnMsg={t('Confirm delete')}
      />
      <ModalBackdropNotFound
        isNotFound={isNotFound}
        setIsNotFound={setIsNotFound}
        message={`${t('Not found for Product Options')}`}
        ButtonMsg="OK"
      />

      <Container fluid>
        <BreadCrumb
          title={t('Product Option Manager')}
          pageTitle={t('Product Option')}
        />
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="mb-0" style={{ fontSize: '1rem' }}>
                      {t('Product Option List')}
                    </h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {/* <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="add-category-btn"
                        disabled={productOptionTotal >= PRODUCT_OPTIOINS_LIMIT}
                        onClick={() => {
                          history('/product-option/add');
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {t('Add Product Option')}
                      </button> */}
                      <MainAddButton
                        to="/product-option/add"
                        label={t('Add Product Option')}
                        id="add-product-option-btn"
                        soft={true}
                        onClick={() => {
                          dispatch(setShowAllValidateErrors(false));
                          dispatch(resetProductOption());
                        }}
                        disabled={productOptionTotal >= PRODUCT_OPTIOINS_LIMIT}
                      />{' '}
                      {/* <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{' '}
                        Export
                      </button>{' '} */}
                    </div>

                    {productOptionTotal >= PRODUCT_OPTIOINS_LIMIT && (
                      <div className="text-danger mt-1">
                        {/* Create up to {PRODUCT_OPTIOINS_LIMIT} items */}
                        {t('Create up to {{item}} items', {
                          item: PRODUCT_OPTIOINS_LIMIT,
                        })}
                      </div>
                    )}
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  {isLoadSuccess ? (
                    <ProductOptionsTable
                      columns={ProductOptionColumns}
                      data={productOptionList || []}
                      customPageSize={PageDetails.entriesPerPage}
                      divClass="table-responsive table-card mb-1 mt-0"
                      tableClass="align-middle table-nowrap"
                      theadClass={`table-${
                        isDarkMode ? 'dark' : 'light'
                      } text-muted text-uppercase`}
                      TotalData={productOptionTotal}
                      totalPages={totalPages}
                      setEntriesPerPage={setEntriesPerPage}
                      currentPage={PageDetails.currentPage}
                      handlePageChange={handlePageChange}
                      // handleSearchFiltersSubmit={handleFilterSearchSubmit}

                      inputPageValue={PageDetails.inputPageValue}
                      setInputPageValue={setInputPageValue}
                    />
                  ) : (
                    <Loader />
                  )}
                </div>
                <ToastContainer closeButton={false} limit={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ProductOptionsPage;
